import clsx from 'clsx'
import {isEmpty} from 'lodash'
import React, {useCallback, useEffect, useState} from 'react'
import {Container, Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import useCancelToken from '../../../../../_gori/hooks/UseCancelToken'
import {Button, ValidationErrorModal} from '../../../../../_gori/partials/widgets'
import {useStatesGlobal} from '../../../../../_gori/helpers/components/StatesGlobalProvider'
import {toAbsoluteUrl} from '../../../../../_gori/helpers'
import {Rates} from '../../../orders'
import {OrdersConfig} from '../../core/_const'
import OrderService from '../../core/_requests'
import {useCarrierNames, useCarrierRateFetcher} from '../../hooks'

type Props = {
  show: boolean
  handleClose: () => void
  disabled?: boolean
  data: any
  handleEditRateLabelSuccess: () => void
  isUpdate?: any
}

const RatesModal: React.FC<Props> = ({
  show,
  handleClose,
  disabled,
  data,
  handleEditRateLabelSuccess,
}) => {
  const intl = useIntl()
  const {statesGlobal} = useStatesGlobal()
  const {newCancelToken, isCancel} = useCancelToken()
  const carrierRouteSettings = statesGlobal.carrierRouteSettings

  const [loading, setLoading] = useState<{getRate: boolean; save: boolean}>({
    getRate: false,
    save: false,
  })
  const [dataRates, setDataRates] = useState<any>()
  const [validationErrors, setValidationErrors] = useState<any>()
  const [chooseRate, setChooseRate] = useState<any>()

  const {carrierNames, disabledCarrierNames} = useCarrierNames({
    isInternational: data?.international,
  })

  const applyChooseRate = useCallback(
    (options: any[]) => {
      const validRates = options.filter((item) => item && !item?.error)
      if (!validRates.length) return null

      const matchingPackages = validRates.filter((item) => item?.matching_package)
      const defaultRate = matchingPackages?.[0] || null

      if (data.service) {
        const matchPkg = validRates.find(
          (item) => item?.matching_package && item?.carrier_service === data.service
        )
        if (matchPkg) return matchPkg

        const matchService = validRates.find((item) => item?.carrier_service === data.service)
        if (matchService) return matchService

        return defaultRate || null
      }

      return defaultRate || null
    },
    [data.service]
  )

  const handleMerge = useCallback(
    (options: any[]) => {
      const chosen = applyChooseRate(options)
      const nextDataRates = {chooseRate: chosen, options}
      setDataRates(nextDataRates)
      if (chosen) setChooseRate(chosen)
    },
    [applyChooseRate]
  )

  const {
    loadingCarriers,
    retryingCarriers,
    fetchAll,
    fetchOne,
    retryOneKeepVisible,
    cancelAll,
    reset,
  } = useCarrierRateFetcher({
    onMerge: handleMerge,
    onError: setValidationErrors,
  })

  const buildPayload = useCallback(
    (carrierName?: string) => ({
      shipment: {
        ...(carrierName ? {carrier: carrierName} : {}),
        international_option: data?.international_option,
        service_options: {
          confirmation: data?.signature_confirmation,
          insured_amount: Number(data?.insurance_option?.insured_amount),
          hazmat: data?.extra_service,
          insurance_type: data?.insurance_option?.provider || null,
        },
        from_address: Object.entries(OrdersConfig.SHIPPING).reduce((fromData, [_key, value]) => {
          fromData[value.value] = data?.from?.[`from_${value.value}`]
          return fromData
        }, {} as Record<string, any>),
        to_address: Object.entries(OrdersConfig.SHIPPING).reduce((toData, [_key, value]) => {
          toData[value.value] = data?.to?.[`to_${value.value}`]
          return toData
        }, {} as Record<string, any>),
        parcel: data?.parcels,
      },
    }),
    [data]
  )

  const handleGetRates = useCallback(async () => {
    cancelAll()
    setLoading((prev) => ({...prev, getRate: true}))
    const initial = reset()
    setDataRates(initial)

    if (!carrierNames.length) {
      setLoading((prev) => ({...prev, getRate: false}))
      return
    }

    const payloads = carrierNames.map((name) => buildPayload(name))
    await fetchAll(payloads)

    setLoading((prev) => ({...prev, getRate: false}))
  }, [carrierNames, buildPayload, fetchAll, cancelAll, reset])

  const fetchAdditionalCarrierRates = useCallback(
    async (carrierName: string) => {
      await fetchOne(buildPayload(carrierName))
    },
    [buildPayload, fetchOne]
  )

  const exploreAllRates = useCallback(async () => {
    setLoading((prev) => ({...prev, getRate: true}))
    await Promise.allSettled(
      disabledCarrierNames.map((carrierName) => fetchOne(buildPayload(carrierName)))
    )
    setLoading((prev) => ({...prev, getRate: false}))
  }, [disabledCarrierNames, buildPayload, fetchOne])

  const retryCarrierRates = useCallback(
    async (carrierName: string) => {
      await retryOneKeepVisible(buildPayload(carrierName))
    },
    [buildPayload, retryOneKeepVisible]
  )

  useEffect(() => {
    if (isEmpty(data)) return
    handleGetRates()
    return () => cancelAll()
  }, [handleGetRates, data, cancelAll])

  const handleEditRate = useCallback(async () => {
    setLoading((prev) => ({...prev, save: true}))
    try {
      const res = await OrderService.patch(
        {
          type: 'service',
          order_ids: [data.id],
          fields: {
            service: chooseRate?.carrier_service,
            package_type: chooseRate?.package_type?.value ?? null,
            estimated_rate: chooseRate?.rate ?? null,
            is_cheapest_rate: chooseRate?.cheapest ?? false,
            preset_id:
              chooseRate?.carrier_service === data.service &&
              chooseRate?.package_type?.value === data.parcels?.package_type
                ? data.preset_id
                : null,
          },
        },
        {cancelToken: newCancelToken()}
      )
      if (res) {
        handleEditRateLabelSuccess()
        handleClose()
      }
    } catch (error: any) {
      if (isCancel(error)) return
      setValidationErrors(error?.response)
    } finally {
      setLoading((prev) => ({...prev, save: false}))
    }
  }, [
    chooseRate,
    data.id,
    data.parcels?.package_type,
    data.preset_id,
    data.service,
    handleClose,
    handleEditRateLabelSuccess,
    isCancel,
    newCancelToken,
  ])

  const handleClickRate = (rate) => {
    setDataRates((prev) => ({
      ...prev,
      chooseRate: rate,
    }))
    setChooseRate(rate)
  }

  const hasLoadedRates = !!dataRates?.options?.some((item) => Number.isFinite(Number(item?.rate)))

  return (
    <>
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => {
            setValidationErrors(undefined)
          }}
          response={validationErrors}
        />
      )}
      <Modal
        id='gori_modal_edit_rate_label'
        tabIndex={-1}
        aria-hidden='true'
        centered
        dialogClassName='modal-size-xl h-auto'
        contentClassName='modal-padding'
        show={show}
        backdrop='static'
        onHide={() => {
          handleClose()
        }}
      >
        <div
          className={clsx('modal-content', {
            'cursor-no-drop': disabled,
          })}
        >
          <Modal.Header closeButton>
            <Modal.Title bsPrefix={'fw-bolder fs-1'}>
              {intl.formatMessage({id: 'RATES'})}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className='vh-75 overflow-auto'>
            <Container
              className={clsx({
                'pe-none': disabled,
              })}
            >
              {!loading.getRate &&
                !hasLoadedRates &&
                !disabledCarrierNames.length &&
                isEmpty(dataRates?.options) && (
                  <div className='text-center fs-5 py-10'>
                    {intl.formatMessage({id: 'NO_SUITABLE_RATE_FOUND'})}
                  </div>
                )}
              <Rates
                data={dataRates}
                handleClickRate={handleClickRate}
                disabled={loading.save || disabled}
                hideNoRates={carrierRouteSettings?.hide_no_rates ?? true}
                isInternational={data?.international ?? false}
                disabledCarriers={disabledCarrierNames}
                loadingCarriers={loadingCarriers}
                retryingCarriers={retryingCarriers}
                onFetchCarrierRates={fetchAdditionalCarrierRates}
                onRetryCarrierRates={retryCarrierRates}
                onExploreAllRates={exploreAllRates}
                isLoadingRates={loading.getRate}
              />
              {loading.getRate && (
                <div
                  className={clsx('d-flex justify-content-center align-items-center', {
                    'h-400px': !hasLoadedRates,
                    'py-12': hasLoadedRates,
                  })}
                >
                  <img
                    src={toAbsoluteUrl('/media/gori/orders/reload.gif')}
                    className='w-150px'
                    alt='reload-rate'
                  />
                </div>
              )}
            </Container>
          </Modal.Body>
          <Modal.Footer>
            <div className='d-flex justify-content-end'>
              <Button
                className='btn btn-advanced-primary'
                label={intl.formatMessage({id: 'SAVE'})}
                loadingText={intl.formatMessage({id: 'SAVE'})}
                event={handleEditRate}
                disabled={loading.getRate || loading.save || isEmpty(chooseRate)}
                loading={loading.save}
              />
            </div>
          </Modal.Footer>
        </div>
      </Modal>
    </>
  )
}

export {RatesModal}
